import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import styled from "styled-components";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import {
  Box,
  CssBaseline,
  Paper as MuiPaper,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { spacing } from "@mui/system";

import GlobalStyle from "../components/GlobalStyle";
import Navbar from "../components/navbar/Navbar";
import {
  userNavsItems,
  adminNavsItems,
  sysAdminNavsItems,
  epamNavsItems,
  serverPamNavsItems,
  epamUserNavsItems,
} from "../components/sidebar/dashboardItems";

import useUserStore from "../services/userStore";
import { NGROK } from "../APIs";
import useNotificationsStore from "../services/notificationStore";
import IdleTimer from "../components/IdleTimer/IdleTimer";
import { myLocalStorage } from "../components/StorageHelper";
import useNotificationStore from "../services/notificationStore";
import { AppSidebar } from "../components/v2components/SideBar/AppSidebar";
import { SidebarProvider, SidebarTrigger } from "../components/ui/sidebar";
import { TooltipProvider } from "../components/ui/tooltip";
import { ThemeToggle } from "../components/v2components/ThemeToggle";
import { ThemeProvider } from "../components/lib/theme-provider";
import { Toaster } from "../components/ui/toaster";
import { Power } from "lucide-react";
import useAuth from "../hooks/useAuth";
import { getClientId, logOut } from "../services/Auth";
import { clearLocalStorageOnLogout } from "../services/Helpers";
import { useKeycloakStore } from "../services/UserService";
import CollapsibleSidebar from "./CollapsibleSidebar";
import ConfirmationDialog from "../components/v2components/Endpoints/ConfirmationDialog";


const Root = styled.div`
  display: flex;
  min-height: 100vh;
`;

const Dashboard = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userAccessMenu, setUserAccessMenu] = useState(null);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const location = useLocation();

  const userData = useUserStore((state) => state.user);
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  let userAccess = userData.role;
  let userRole = !userData.userRole ? "" : userData.userRole;

  const {
    setUserAWSApprovedNotifications,
    setUserAzureApprovedNotifications,
    setUserK8ApprovedNotifications,
    setUserApprovedNotifications,
    setUserGCPApprovedNotifications,
    setUserWebUrlApprovedNotifications,
    setTotalPendingCount,
  } = useNotificationStore();

  const { local_kc } = useKeycloakStore();
  const { signOut } = useAuth();
  const email = userData.email || myLocalStorage.getItem("email");
  const logoutUri = myLocalStorage.getItem("logoutUri");
  const access_token = myLocalStorage.getItem("access_token");
  const refresh_token = myLocalStorage.getItem("refresh_token");
  const [isFetching, setIsFetching] = useState(false);

  const navigate = useNavigate();

  const logOutAsInitialAdmin = () => {
    const realm = myLocalStorage.getItem("realm");
    signOut({
      realm,
      client_id: getClientId(realm),
      access_token,
      refresh_token,
    });

    navigate("/");
  };

  const logoutAsUser = () => {
    window.open(logoutUri, "_self");
    myLocalStorage.removeItem("logoutUri");
  };

  const logOutOnClientAndServer = () => {
    const isUserLoginedInitialAdmin = !logoutUri;

    try {
      if (isUserLoginedInitialAdmin) {
        logOutAsInitialAdmin();
      } else {
        logoutAsUser();
      }
    } catch (error) {
      local_kc && local_kc.doLogout();
      console.error(error);
    } finally {
      clearLocalStorageOnLogout();
    }
  };

  const openLogoutConfirmation = () => {
    setIsLogoutConfirmOpen(true);
  };

  const handleLogoutDialogChange = (open) => {
    if (!open && isLoggingOut) return;
    setIsLogoutConfirmOpen(open);
  };

  const handleSignOut = async () => {
    setIsLoggingOut(true);
    try {
      await webURlCallToSave();
      await logOut(email);

      await logOutOnClientAndServer();
    } catch (error) {
      await webURlCallToSave();
      logOutOnClientAndServer();
      console.error(error);
    } finally {
      setIsLoggingOut(false);
      setIsLogoutConfirmOpen(false);
    }
  };
  const webURlCallToSave = async () => {
    try {
      await axios.post("http://localhost:5051/webvideo-save-on-logout");
    } catch (error) {
      console.error("Error in webURlCallToSave:", error);
    }
  };
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const checkUserExists = useCallback(async () => {
    console.log("Checking user existence...");
    try {
      const response = await axios.get(`${NGROK}/api/find-by-email`, {
        params: {
          email: userData.email,
        },
      });
      if (!response.data) {
        console.warn("User does not exist in the current tenant.");
        logOut(userData.email)
              .then(() => {
                clearLocalStorageOnLogout();
                navigate("/signIn");
              })
              .catch((error) => {
                console.error("An error occurred during logout:", error);
              });
      }
    } catch (error) {

      console.error("Error checking user existence:", error);
    }
  }, [userData.email, latestTenant]);


  const fetchSummary = useCallback(async () => {
    if (isFetching) return; // ✅ Prevent re-entry
    setIsFetching(true);

    const controller = new AbortController();

    try {
      const email = userData.email;
      const params = {
        role: userAccess,
        tenantName: latestTenant,
      };

      if (userAccess === "TENANT_USER") {
        params.email = email;
      }

      const response = await axios.get(`${NGROK}/api/access/summary`, {
        params,
        signal: controller.signal,
      });

      if (response.data) {
        let {
          totalPendingCount,
          onPremAccess,
          awsDataAccesses,
          urlCredentialAccesses,
          azureAccess,
          k8Access,
          gcpDataAccesses,
        } = response.data;

        setTotalPendingCount(totalPendingCount);
        setUserAWSApprovedNotifications(awsDataAccesses);
        setUserAzureApprovedNotifications(azureAccess);
        setUserK8ApprovedNotifications(k8Access);
        setUserApprovedNotifications(onPremAccess);
        setUserGCPApprovedNotifications(gcpDataAccesses);
        setUserWebUrlApprovedNotifications(urlCredentialAccesses);
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        console.warn("Fetch summary request canceled");
      } else {
        console.error("Error fetching summary:", error);
      }
    } finally {
      setIsFetching(false);
    }
  }, [
    userData.email,
    userAccess,
    latestTenant
  ]);

  const fetchConfig = useCallback(async () => {
    try {
      const [admin, user] = await Promise.all([
        axios.get(`${NGROK}/api/get-all-cloudConfig?tenantName=${latestTenant}`),
        axios.get(`${NGROK}/api/get-all-usercloudConfig?tenantName=${latestTenant}&email=${userData.email}`),
      ]);

      const configData = {
        awsConfig: admin.data.awsConfig,
        azureConfig: admin.data.azureConfig,
        gcpConfig: admin.data.gcpConfig,
        awsUserConfig: user.data.awsUserConfig,
        azureUserConfig: user.data.azureUserConfig,
        gcpUserConfig: user.data.gcpUserConfig,
      };

      const setConfigs = useNotificationsStore.getState().setCloudConfigs;
      setConfigs(configData);
    } catch {
      return {};
    }
  }, [latestTenant, userData.email]);


  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  useEffect(() => {
    if (userRole === "epam-user") {
      setUserAccessMenu(epamUserNavsItems);
    } else if (userRole === "epam-admin") {
      setUserAccessMenu(epamNavsItems);
    } else if (userRole === "server-pam-admin") {
      setUserAccessMenu(serverPamNavsItems);
    } else if (userAccess === "TENANT_ADMIN") {
      setUserAccessMenu(adminNavsItems);
    } else if (userAccess === "SYS_ADMIN") {
      setUserAccessMenu(sysAdminNavsItems);
    } else if (userAccess === "TENANT_USER") {
      setUserAccessMenu(userNavsItems);
    }
  }, [userAccess, userRole]);

  useEffect(() => {
    if (!userData?.email || !latestTenant) return;

    fetchConfig();
    fetchSummary();
    checkUserExists();

    const interval = setInterval(() => {
      fetchConfig();
      fetchSummary();
      checkUserExists();
    }, 5000); 

    return () => {
      clearInterval(interval);
    };
  }, [fetchConfig, fetchSummary, userData.email, latestTenant]);



  useEffect(() => {
    if (location.pathname === "/dashboard/cloud-analytics") {
      window.open("/cloud-analytics-view", "_blank");
      navigate("/dashboard/overview");
    }
  }, [location.pathname]);

  return (
    <ThemeProvider defaultTheme="system" storageKey="device-management-theme">
      <Toaster />
      <Root>
        <IdleTimer />
        <TooltipProvider>
          <div className="flex w-full h-screen">
            <CollapsibleSidebar menuList={userAccessMenu} />
            <div className="flex min-w-0 flex-col flex-1">
              <header className="flex items-center justify-between p-2 border-b bg-background">
                <div />
                <div className="flex items-center gap-2 mr-4">
                  <ThemeToggle />
                  <button
                    className="p-1 rounded hover:bg-muted"
                    onClick={openLogoutConfirmation}
                    title="Logout"
                  >
                    <Power className="w-5 h-5 text-red-400" />
                  </button>
                </div>
              </header>
              <ConfirmationDialog
                open={isLogoutConfirmOpen}
                onOpenChange={handleLogoutDialogChange}
                title="Confirm Sign Out"
                description="Are you sure you want to sign out?"
                confirmText="Sign out"
                cancelText="Cancel"
                variant="destructive"
                loading={isLoggingOut}
                onConfirm={handleSignOut}
              />
              <main className="min-w-0 flex-1 overflow-auto scrollbar-thin">
                {children || <Outlet />}
              </main>
            </div>
          </div>
        </TooltipProvider>
      </Root>
    </ThemeProvider>
  );
};

export default Dashboard;